import dayjs from 'dayjs';
import SuperFetch from 'library/helpers/superFetch';
import { removeEmptyOrNull } from 'library/helpers/utility';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { devicesActions } from 'reduxStore/devices/slice';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { accessActions } from 'reduxStore/access/slice';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { partnerActions } from 'reduxStore/partner/slice';
import { peopleActions } from 'reduxStore/people/slice';
import { roleActions } from 'reduxStore/roles/slice';
import { teamsActions } from 'reduxStore/teams/slice';
import { accessLogsActions } from './slice';
import { visitorActivityCacheKey } from './visitorActivityParams';
import { logsStatsCacheKey } from './statsSelectors';
import { logsWindowCacheKey } from './windowSelectors';

// 31 days covers the full month: matches AccessLogCard's "last month" default and the SiteCard
// sparkline's This week / Last week / Month views.

const defaultFilters = {
	page: 1,
	pageSize: 15,
	datePreset: 'today',
	personIDs: [],
	deviceIDs: [],
	startDate: dayjs().startOf('day').toISOString(),
	endDate: dayjs().endOf('day').toISOString()
};

// Fetches access logs for the active org. The backend already scopes by orgID and permissions,
// so deviceIDs are sent only when the user filters by device.
function* fetchAccessLogs() {
	const { orgID } = yield select((state) => state.Auth.selectedProfile);
	const selectedSiteID = yield select((state) => state.App.selectedSiteID);
	const siteID = selectedSiteID ?? 'all';

	yield put(accessLogsActions.loadAccessLogsStart({ orgID, siteID }));

	try {
		const filters = (yield select((state) => state.AccessLogs.filters[orgID])) || {
			...defaultFilters
		};

		const {
			page = 1,
			pageSize = 50,
			startDate: dateFrom,
			endDate: dateTo,
			personIDs = [],
			deviceIDs = [],
			status,
			integrationID
		} = filters;

		const params = removeEmptyOrNull({
			orgID,
			page,
			pageSize,
			siteID: siteID !== 'all' ? siteID : undefined,
			dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
			dateTo: dateTo ? new Date(dateTo).toISOString() : undefined,
			personIDs: personIDs.length ? personIDs.join(',') : undefined,
			deviceIDs: deviceIDs.length ? deviceIDs.join(',') : undefined,
			status: status && status !== 'All' ? status.toLowerCase() : undefined,
			integrationID: integrationID || undefined
		});

		const logs = yield call(() => SuperFetch.get('/logs/accessLogs', {}, params));

		if (logs.statusCode === 200) {
			yield put(
				accessLogsActions.loadAccessLogsSuccess({
					orgID,
					siteID,
					data: logs.data,
					total: Number(logs.pagination?.total) || 0,
					// Stamped here rather than in the reducer so the reducer stays pure.
					loadedAt: Date.now()
				})
			);
		} else {
			throw new Error(logs.message || 'Failed to load access logs.');
		}
	} catch (error) {
		yield put(accessLogsActions.loadAccessLogsError({ orgID, siteID, error: error.message }));
	}
}

// Daily rollup — reuses fetchTodayCounts (single set of API calls) and
// reshapes the result into the dailyByOrg format that useSiteRollups reads.
const DAILY_ROLLUP_TTL_MINUTES = 5;

export function* fetchAccessLogDailyRollup() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		if (!orgID) return;

		// Skip if fresh cache exists
		const { dailyByOrg, dailyTimeStamp } = yield select((state) => state.AccessLogs);
		const cached = dailyByOrg[orgID];
		if (cached && Object.keys(cached).length > 0 && dailyTimeStamp) {
			const age = dayjs().diff(dayjs(dailyTimeStamp), 'minute');
			if (age < DAILY_ROLLUP_TTL_MINUTES) return;
		}

		// Delegate to fetchTodayCounts — it does one request per site using the
		// backend's native siteID param (no deviceIDs in the URL).
		yield call(fetchTodayCounts);

		// Read the freshly loaded today counts and reshape into dailyByOrg format
		const dataToday = yield select((state) => state.AccessLogs.dataToday[orgID]);
		if (!dataToday) return;

		const todayKey = dayjs().format('YYYY-MM-DD');
		const bySite = {};
		for (const [siteID, count] of Object.entries(dataToday)) {
			if (siteID === 'all') continue;
			bySite[siteID] = { [todayKey]: count };
		}

		yield put(accessLogsActions.loadAccessLogDailyRollupSuccess({ orgID, bySite }));
	} catch (error) {
		yield put(accessLogsActions.loadAccessLogDailyRollupError(error.message || String(error)));
	}
}

// Per-site + org-wide unlock counts for today in ONE request (same scope as /accessLogs); socket-refreshed.
// Returns { all, bySite: { [siteID]: n } }, flattened to the { all, [siteID]: n } map the slice reads.
export function* fetchTodayCounts() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		if (!orgID) return;

		yield put(accessLogsActions.loadTodayCountsStart());

		const dateFrom = dayjs().startOf('day').toISOString();
		const dateTo = dayjs().endOf('day').toISOString();

		const res = yield call(() =>
			SuperFetch.get('/logs/accessLogs/today-counts', {}, removeEmptyOrNull({ dateFrom, dateTo }))
		);

		const payload = res?.data || {};
		const counts = { all: Number(payload.all) || 0 };
		for (const [siteID, cnt] of Object.entries(payload.bySite || {})) {
			counts[siteID] = Number(cnt) || 0;
		}

		yield put(accessLogsActions.loadTodayCountsSuccess({ orgID, counts }));
	} catch {
		yield put(accessLogsActions.loadTodayCountsError());
	}
}

// Daily unlock counts for a scope ('all' or siteID); 14 days covers the Sites pulse WoW. Bucketed by LOCAL
// day via tzOffsetMinutes since the server stores EventAt in UTC. Cached per scope by TTL.
const SERIES_DAYS = 14;
const SERIES_TTL_MINUTES = 5;

// In-flight de-dup: a TTL cache alone lets concurrent same-scope dispatches all fetch (cache still empty).
// Home's UnlockTrend and the Sites pulses dispatch together, so keys with a running request short-circuit.
const dailySeriesInFlight = new Set();

export function* fetchDailySeries(action) {
	const scopeKey = action?.payload ?? 'all';
	let inFlightKey;
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		if (!orgID) return;

		const cached = yield select((state) => state.AccessLogs.dailySeries?.[orgID]?.[scopeKey]);
		if (cached?.series && cached.ts && dayjs().diff(dayjs(cached.ts), 'minute') < SERIES_TTL_MINUTES) {
			return;
		}

		const key = `${orgID}:${scopeKey}`;
		if (dailySeriesInFlight.has(key)) return;
		dailySeriesInFlight.add(key);
		inFlightKey = key; // set only after WE claim it, so the finally never clears another run's key

		const dateFrom = dayjs()
			.subtract(SERIES_DAYS - 1, 'day')
			.startOf('day')
			.toISOString();
		const dateTo = dayjs().endOf('day').toISOString();
		const tzOffsetMinutes = dayjs().utcOffset();

		const res = yield call(() =>
			SuperFetch.get(
				'/logs/accessLogs/daily-series',
				{},
				removeEmptyOrNull({
					siteID: scopeKey !== 'all' ? scopeKey : undefined,
					dateFrom,
					dateTo,
					tzOffsetMinutes
				})
			)
		);

		// Backend returns { 'YYYY-MM-DD': count } for days with activity; consumers
		// iterate the last N days and default missing days to 0.
		const series = res?.data || {};

		yield put(accessLogsActions.loadDailySeriesSuccess({ orgID, scopeKey, series }));
	} catch (error) {
		yield put(accessLogsActions.loadDailySeriesError(error.message || String(error)));
	} finally {
		if (inFlightKey) dailySeriesInFlight.delete(inFlightKey);
	}
}

// Every access log in a window from /logs/accessLogs, cached per (scope, window).
// Paged in FULL so counts match pagination.total; MAX_PAGES caps at 25k, then truncates.
const LOGS_WINDOW_TTL_MINUTES = 5;
const LOGS_WINDOW_PAGE_SIZE = 1000;
const LOGS_WINDOW_MAX_PAGES = 25;
// Pages 2..N go out in groups this size.
const LOGS_WINDOW_CONCURRENCY = 4;

// Same in-flight de-dup as dailySeriesInFlight; matters most here since a duplicate multiplies the whole
// paged fan-out (up to LOGS_WINDOW_MAX_PAGES). The Lockers dashboard is its consumer.
const logsWindowInFlight = new Set();

export function* fetchLogsWindow(action) {
	const scopeKey = action?.payload?.scopeKey ?? 'all';
	const days = action?.payload?.days ?? 7;
	// A socket-driven refresh must outrank the TTL: the event IS the news that the
	// cache is stale. The in-flight guard below still applies.
	const force = action?.payload?.force ?? false;
	const cacheKey = logsWindowCacheKey(scopeKey, days);
	let inFlightKey;
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		if (!orgID) return;

		const cached = yield select((state) => state.AccessLogs.logsWindow?.[orgID]?.[cacheKey]);
		if (!force && cached?.logs && cached.ts && dayjs().diff(dayjs(cached.ts), 'minute') < LOGS_WINDOW_TTL_MINUTES) {
			return;
		}

		const key = `${orgID}:${cacheKey}`;
		if (logsWindowInFlight.has(key)) return;
		logsWindowInFlight.add(key);
		inFlightKey = key; // set only after WE claim it, so the finally never clears another run's key

		const dateFrom = dayjs()
			.subtract(days - 1, 'day')
			.startOf('day')
			.toISOString();
		const dateTo = dayjs().endOf('day').toISOString();

		const baseParams = removeEmptyOrNull({
			orgID,
			siteID: scopeKey !== 'all' ? scopeKey : undefined,
			dateFrom,
			dateTo
		});

		const pageParams = (page) => ({ ...baseParams, page, pageSize: LOGS_WINDOW_PAGE_SIZE });
		const readPage = (res) => {
			if (res.statusCode !== 200) throw new Error(res.message || 'Failed to load the access-log window.');
			return res.data || [];
		};

		// Page 1 alone: its `pagination.total` is what says how many more there are.
		const first = yield call(SuperFetch.get, '/logs/accessLogs', {}, pageParams(1));
		let logs = readPage(first);
		const total = Number(first.pagination?.total) || logs.length;

		// Was one `yield call` per page in a for loop — up to 25 round trips strictly in
		// series, on the path that blocks the Lockers loading screen. Now only page 1 is.
		const neededPages = Math.ceil(total / LOGS_WINDOW_PAGE_SIZE) || 1;
		const lastPage = Math.min(neededPages, LOGS_WINDOW_MAX_PAGES);
		// "Finished the window" vs "ran out of pages" — one that fits exactly is not truncated.
		const exhausted = neededPages <= LOGS_WINDOW_MAX_PAGES;

		// Batched, not one `all` over every page: the cap allows 25, and firing them together
		// trades a slow load for a hammered server. `all` keeps order, which EventAt DESC needs.
		for (let page = 2; page <= lastPage; page += LOGS_WINDOW_CONCURRENCY) {
			const batch = [];
			for (let p = page; p < page + LOGS_WINDOW_CONCURRENCY && p <= lastPage; p += 1) {
				batch.push(call(SuperFetch.get, '/logs/accessLogs', {}, pageParams(p)));
			}
			const responses = yield all(batch);
			logs = logs.concat(...responses.map(readPage));
		}

		yield put(accessLogsActions.loadLogsWindowSuccess({ orgID, cacheKey, logs, total, truncated: !exhausted }));
	} catch (error) {
		yield put(accessLogsActions.loadLogsWindowError(error.message || String(error)));
	} finally {
		if (inFlightKey) logsWindowInFlight.delete(inFlightKey);
	}
}

const LOGS_STATS_PATH = {
	people: '/logs/people/stats',
	visitors: '/logs/visitors/stats',
	credentials: '/logs/credentials/stats',
	activity: '/logs/activity/stats',
	teams: '/logs/teams/stats',
	groups: '/logs/groups/stats',
	roles: '/logs/roles/stats'
};
const LOGS_STATS_TTL_MINUTES = 2;

// Stat-card aggregates computed by the App Server (org timezone, its own unlock rule, no row cap).
export function* fetchLogsStats({ payload } = {}) {
	const { kind, scopeKey = 'all', days = 30, floorID, force = false } = payload || {};
	const { orgID } = yield select((state) => state.Auth.selectedProfile);
	if (!orgID || !LOGS_STATS_PATH[kind]) return;
	const cacheKey = logsStatsCacheKey(kind, scopeKey, days, floorID);
	const cached = yield select((state) => state.AccessLogs.stats?.[orgID]?.[cacheKey]);
	if (!force && cached?.data && cached.ts && dayjs().diff(dayjs(cached.ts), 'minute') < LOGS_STATS_TTL_MINUTES)
		return;
	// Already on its way (a second mount, a quick site flip back): one request per key.
	if (!force && cached?.loading) return;

	yield put(accessLogsActions.loadLogsStatsStart({ orgID, cacheKey }));
	try {
		const query = removeEmptyOrNull({ siteID: scopeKey !== 'all' ? scopeKey : undefined, floorID, days });
		const res = yield call(SuperFetch.get, LOGS_STATS_PATH[kind], {}, query);
		if (res.statusCode !== 200) throw new Error(res.error?.message || res.error || 'Failed to load stats.');
		yield put(accessLogsActions.loadLogsStatsSuccess({ orgID, cacheKey, data: res.data }));
	} catch (error) {
		yield put(accessLogsActions.loadLogsStatsError({ orgID, cacheKey, error: error.message || String(error) }));
	}
}

const VISITOR_ACTIVITY_TTL_MINUTES = 1;

// One page of the server-filtered visitor activity feed (visitor-attributed rows, newest first).
export function* fetchVisitorActivity({ payload } = {}) {
	const { scopeKey = 'all', params = {}, force = false } = payload || {};
	const { orgID } = yield select((state) => state.Auth.selectedProfile);
	if (!orgID) return;
	const cacheKey = visitorActivityCacheKey(scopeKey, params);
	const cached = yield select((state) => state.AccessLogs.visitorActivity?.[orgID]?.[cacheKey]);
	if (!force && cached?.loading) return;
	if (!force && cached?.ts && dayjs().diff(dayjs(cached.ts), 'minute') < VISITOR_ACTIVITY_TTL_MINUTES) return;

	yield put(accessLogsActions.loadVisitorActivityStart({ orgID, cacheKey }));
	try {
		const query = { ...(scopeKey !== 'all' ? { siteID: scopeKey } : {}), ...params };
		const res = yield call(SuperFetch.get, '/logs/visitors/activity', {}, query);
		if (res.statusCode !== 200)
			throw new Error(res.error?.message || res.error || 'Failed to load visitor activity.');
		const rows = res.data || [];
		yield put(
			accessLogsActions.loadVisitorActivitySuccess({
				orgID,
				cacheKey,
				rows,
				total: Number(res.pagination?.total) || rows.length
			})
		);
	} catch (error) {
		yield put(
			accessLogsActions.loadVisitorActivityError({ orgID, cacheKey, error: error.message || String(error) })
		);
	}
}

// Marks these kinds' cached stats stale instead of refetching: the queries are heavy, so only the
// cards on screen reload (useLogsStats); the rest reload when next shown.
function* markStatsStale(kinds) {
	const { orgID } = yield select((state) => state.Auth.selectedProfile);
	yield put(accessLogsActions.invalidateLogsStats({ orgID, kinds }));
}

// Any person mutation (add, invite, delete, enable/disable, Manage Access) moves the people, visitors,
// team-coverage, role and credential-assignment counts.
export function* invalidatePeopleStats() {
	yield call(markStatsStale, ['people', 'visitors', 'teams', 'roles', 'credentials']);
}

// Credential and Cloud Key mutations move the Credentials cards and the visitors' credential coverage.
export function* invalidateCredentialsStats() {
	yield call(markStatsStale, ['credentials', 'visitors']);
}

// Team, membership and grant changes move the Teams stat cards.
export function* invalidateTeamsStats() {
	yield call(markStatsStale, ['teams']);
}

// Group, membership and grant changes move the Groups stat cards.
export function* invalidateGroupsStats() {
	yield call(markStatsStale, ['groups']);
}

// Role assignments and installer grants move the Roles stat cards.
export function* invalidateRolesStats() {
	yield call(markStatsStale, ['roles']);
}

// Grant/revoke actions: they move both the Teams and the Groups stat cards.
const PERMISSION_CHANGES = [
	accessActions.grantPermissionSuccess,
	accessActions.revokePermissionSuccess,
	accessActions.revokePermissionByIDsSuccess,
	accessActions.savePermissionChangesSuccess
];

export default function* accessLogsSaga() {
	yield takeLatest(accessLogsActions.loadAccessLogs, fetchAccessLogs);
	yield takeLatest(accessLogsActions.setAccessLogsFilters, fetchAccessLogs);
	// Scope changes come via onScopeChange (app/saga.js); listening to setSelectedSite here duplicated fetches.
	// Daily rollup delegates to fetchTodayCounts; triggered on loadDevicesSuccess so sites are available.
	yield takeLatest(accessLogsActions.loadAccessLogDailyRollup, fetchAccessLogDailyRollup);
	yield takeLatest(devicesActions.loadDevicesSuccess, fetchAccessLogDailyRollup);
	yield takeLatest(accessLogsActions.loadTodayCounts, fetchTodayCounts);
	// takeEvery (not takeLatest) so switching scope doesn't cancel an in-flight
	// fetch for a different scope; the per-scope TTL cache dedupes repeats.
	yield takeEvery(accessLogsActions.loadDailySeries, fetchDailySeries);
	// takeEvery so several windows (7d, 14d, the Lockers 90d) can load without
	// cancelling each other; the per-key TTL cache dedupes repeats.
	yield takeEvery(accessLogsActions.loadLogsWindow, fetchLogsWindow);
	// takeEvery: People and Visitors (and each site) keep their own cache key.
	yield takeEvery(accessLogsActions.loadLogsStats, fetchLogsStats);
	yield takeLatest(
		[
			peopleActions.createPeopleSuccess,
			peopleActions.createVisitorsSuccess,
			peopleActions.bulkAddPeopleDone,
			peopleActions.deletePersonSuccess,
			peopleActions.enablePersonSuccess,
			peopleActions.disablePersonSuccess,
			peopleActions.manageAccessBulkSuccess
		],
		invalidatePeopleStats
	);
	yield takeLatest(
		[
			teamsActions.addTeamSuccess,
			teamsActions.updateTeamSuccess,
			teamsActions.deleteTeamSuccess,
			teamsActions.addTeamMemberSuccess,
			teamsActions.deleteTeamMemberSuccess,
			...PERMISSION_CHANGES
		],
		invalidateTeamsStats
	);
	yield takeLatest(
		[
			assetGroupActions.addGroupSuccess,
			assetGroupActions.updateGroupSuccess,
			assetGroupActions.deleteGroupSuccess,
			assetGroupActions.addGroupAssetSuccess,
			assetGroupActions.removeGroupAssetSuccess,
			assetGroupActions.linkChildGroupsSuccess,
			assetGroupActions.unlinkChildGroupsSuccess,
			...PERMISSION_CHANGES
		],
		invalidateGroupsStats
	);
	yield takeLatest(
		[
			roleActions.assignRoleSuccess,
			roleActions.revokeRoleSuccess,
			partnerActions.createGrantSuccess,
			partnerActions.revokeGrantSuccess,
			partnerActions.renewGrantSuccess
		],
		invalidateRolesStats
	);
	yield takeLatest(
		[
			credentialsActions.createCredentialSuccess,
			credentialsActions.deleteCredentialSuccess,
			credentialsActions.deleteCredentialsSuccess,
			credentialsActions.assignCredentialSuccess,
			credentialsActions.unassignCredentialSuccess,
			credentialsActions.associateCardSuccess,
			credentialsActions.createCardsBatchIRSuccess,
			credentialsActions.createCardsBatchSuccess,
			credentialsActions.createCardsBatchInventorySuccess,
			credentialsActions.issueCloudKeySuccess,
			credentialsActions.revokeCloudKeySuccess
		],
		invalidateCredentialsStats
	);
	// The key exists even when its link failed to send.
	yield takeLatest(
		(a) => a.type === credentialsActions.issueCloudKeyError.type && a.payload?.credentialID,
		invalidateCredentialsStats
	);
	// takeEvery + the in-flight guard: takeLatest would cancel the request the guard is waiting on.
	yield takeEvery(accessLogsActions.loadVisitorActivity, fetchVisitorActivity);
}
